import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { isSupabaseConfigured } from "../lib/supabaseClient";
import * as authApi from "../lib/auth";
import { clearCompanyCache } from "../lib/db";
import { ensureChartOfAccounts } from "../lib/chartOfAccounts";
import { getBillingState } from "../lib/billing";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [membership, setMembership] = useState(null); // { company_id, role, company_name }
  // LT-2: { state, trialEndsAt, daysLeft, status, hasCustomer }. Starts null
  // and STAYS null on failure — the banners render nothing rather than
  // guessing, because a wrong "read-only" banner on a paying customer's screen
  // is worse than no banner at all. The real gate is can_write() server-side.
  const [billing, setBilling] = useState(null);

  const loadBilling = useCallback(async (companyId) => {
    if (!companyId) { setBilling(null); return; }
    try {
      setBilling(await getBillingState(companyId));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[billing] could not load the billing state:", e.message);
      setBilling(null);
    }
  }, []);

  const loadMembership = useCallback(async () => {
    try {
      clearCompanyCache();
      const m = await authApi.getMyMembership();
      setMembership(m);
      if (m?.company_id) {
        const companyId = m.company_id;
        // Business-settings read is best-effort and deliberately NOT awaited
        // here — companyName arriving a beat later is fine, but it must never
        // block or delay session/membership resolution above. Precedence
        // (trading_name -> legal_name -> companies.name) is mirrored
        // server-side in 20260730120000_invite_preview_business_name.sql; the
        // two must not drift.
        (async () => {
          try {
            const name = await authApi.getBusinessName(companyId);
            if (name) {
              // Guard against a stale resolution landing after the user has
              // since signed in as someone else (a different company) —
              // only apply it if the membership it was resolved for is still
              // the current one.
              setMembership((cur) => (cur && cur.company_id === companyId ? { ...cur, company_name: name } : cur));
            }
          } catch {
            // Network, RLS, or no business_settings row yet — fall back
            // silently to the company_name already set above from
            // companies.name. A wrong-but-present name beats a blank screen.
          }
        })();

        // LT-2: the billing state. AWAITED, unlike the two fire-and-forget
        // calls below — a read-only banner that appears a second after the
        // page does is a banner that flickers in under the user's cursor.
        // One indexed lookup on a primary key; it is not a meaningful delay.
        await loadBilling(companyId);

        // ── P4-CC26c-0b-2: the chart of accounts, at first authenticated contact ──
        // It cannot be created at signup: auth.uid() is NULL inside
        // handle_new_user() (measured), so current_company_id() cannot resolve and
        // set_company_from_auth() refuses. This is the earliest moment a company
        // exists AND a session exists, so this is where it happens.
        //
        // Fire-and-forget, exactly like the business-name read above and for the
        // same reason: a new company's chart arriving a beat later is fine, but it
        // must never block or delay session/membership resolution. Nothing on
        // screen depends on it having finished.
        //
        // The idempotency, the permission tolerance and the company check all live
        // in the data layer, where the verification estate can reach them
        // (supabase/verify/gl_chart_bootstrap.verify.mjs). This is only the call.
        //
        // The catch is NOT a catch-all in disguise: ensureChartOfAccounts()
        // already swallows the one code that is not a fault (42501, a user
        // without write on accounts) and returns a skipped result for it.
        // Anything reaching here is a REAL failure, so it is warned about rather
        // than hidden — the same treatment as a membership load failure below.
        (async () => {
          try {
            await ensureChartOfAccounts(companyId);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[accounts] could not ensure the chart of accounts:", e.message);
          }
        })();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[auth] could not load membership:", e.message);
      setMembership(null);
      setBilling(null);
    }
  }, [loadBilling]);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    let unsub = () => {};
    (async () => {
      const s = await authApi.getSession();
      setSession(s);
      if (s) await loadMembership();
      setLoading(false);
      unsub = authApi.onAuthChange(async (next) => {
        setSession(next);
        if (next) await loadMembership();
        else { setMembership(null); setBilling(null); }
      });
    })();
    return () => unsub();
  }, [loadMembership]);

  const value = {
    loading,
    isConfigured: isSupabaseConfigured,
    session,
    user: session?.user || null,
    companyId: membership?.company_id || null,
    companyName: membership?.company_name || null,
    role: membership?.role || null,
    signIn: authApi.signIn,
    signUp: authApi.signUp,
    signOut: authApi.signOut,
    // Re-resolves company_id/role/companyName without a page reload — e.g.
    // after Business Settings saves a corrected name (CC16), so the Atlas
    // greeting and anything else reading companyName picks it up immediately.
    refreshMembership: loadMembership,
    // LT-2
    billing,
    // Re-reads the billing state without a reload — called after a Stripe
    // redirect lands back on /billing, where the webhook may still be in
    // flight a second behind the browser.
    refreshBilling: () => loadBilling(membership?.company_id || null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
